import React, { useEffect, useState } from "react";
import Modal from "../../../ui/Modal";
import { fetchWithAuth } from "../../../../utils/api";
import Navbar from "../../../ui/Navbar";
import { swalError, swalSuccess } from "../../../../utils/swalAlert";
import Loader from "../../../ui/Loader";
import Table from "../../../ui/Table";
import isEqual from "lodash/isEqual";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageTerms = () => {
  // State for list of terms
  const [terms, setTerms] = useState([]);

  // State for creating a new term
  const initialTerm = {
    version: "",
  };
  const [newTerm, setNewTerm] = useState(initialTerm);
  const [newTermFile, setNewTermFile] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // State for editing a term
  const initialEditTerm = {
    _id: "",
    version: "",
  };
  const [selectedTerm, setSelectedTerm] = useState(initialEditTerm);
  const [editTerm, setEditTerm] = useState(initialEditTerm);
  const [editTermFile, setEditTermFile] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", id: "home" },
    {
      name: "Manage Terms",
      path: "/superadmin/manage-terms",
      isLast: true,
      id: "manage-terms",
    },
  ];

  const handleFetchAllTerms = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/term/get-all`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        const error = data;
        throw new Error(error.message || "Failed to fetch terms");
      }
      setTerms(data);
    } catch (error) {
      swalError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleFetchAllTerms();
  }, []);

  const handleCreateModalOpen = () => {
    setIsCreateModalOpen(true);
    setNewTerm(initialTerm);
    setNewTermFile(null);
    setIsSubmitting(false);
  };
  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    setNewTerm(initialTerm);
    setNewTermFile(null);
    setIsSubmitting(false);
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setNewTerm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handlecreateFileChange = (e) => {
    const { files } = e.target;
    const file = files[0];
    setNewTermFile(file);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("version", newTerm.version);
    if (newTermFile) formData.append("file", newTermFile);
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/term/create`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        const error = data;
        throw new Error(error.message || "Failed to create term");
      }
      swalSuccess(data.message || "Term created successfully!");
      handleCreateModalClose();
      handleFetchAllTerms();
    } catch (error) {
      swalError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditModalOpen = (term) => {
    setIsEditModalOpen(true);
    setSelectedTerm(term);
    setEditTerm(term);
    setEditTermFile(null);
    setIsSubmitting(false);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedTerm(initialEditTerm);
    setEditTerm(initialEditTerm);
    setEditTermFile(null);
    setIsSubmitting(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditTerm((prev) => ({ ...prev, [name]: value }));
  };
  const handleEditFileChange = (e) => {
    const { files } = e.target;
    const file = files[0];
    setEditTermFile(file);
  };

  const isEditModalChanged = !isEqual(editTerm, selectedTerm) || editTermFile;

  const getChangedFields = (original, updated) => {
    const changes = {};
    if (original.version !== updated.version && updated.version !== "") {
      changes.version = updated.version;
    }
    if (original.content !== updated.content && updated.content !== "") {
      changes.content = updated.content;
    }
    return changes;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const changes = getChangedFields(selectedTerm, editTerm);
    if (Object.keys(changes).length === 0 && !editTermFile) {
      swalError("No changes made to the term.");
      setIsSubmitting(false);
      return;
    }
    const formData = new FormData();
    if (changes.version) formData.append("version", changes.version);
    if (editTermFile) formData.append("file", editTermFile);

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/term/update/${selectedTerm?._id}`,
        {
          method: "PUT",
          credentials: "include",
          body: formData,
        }
      );
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        const error = data;
        throw new Error(error.message || "Failed to update term");
      }
      swalSuccess(data.message || "Term updated successfully!");
      handleEditModalClose();
      handleFetchAllTerms();
    } catch (error) {
      swalError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      key: "version",
      label: "Version",
      sortable: true,
      render: (row) => (
        <span className="status-badge badge-requested">{row.version || "N/A"}</span>
      ),
    },
    {
      key: "fileName",
      label: "File Name",
      sortable: true,
      render: (row) =>
        row.fileName ? (
          <span>{row.fileName}</span>
        ) : (
          <span style={{ color: "var(--text-muted, #888)" }}>No file</span>
        ),
    },
    {
      key: "fileURL",
      label: "Term File",
      sortable: false,
      render: (row) =>
        row.fileURL ? (
          <a
            href={row.fileURL}
            target="_blank"
            rel="noopener noreferrer"
            className="contract-link"
          >
            <i className="fa-solid fa-file-pdf" style={{ marginRight: "6px" }}></i>
            View PDF
          </a>
        ) : (
          <span style={{ color: "var(--text-muted, #888)" }}>No file uploaded</span>
        ),
    },
    {
      key: "edit",
      label: "",
      sortable: false,
    },
  ];

  const enhancedTerms = terms.map((term) => ({
    ...term,
    edit: (
      <button
        className="edit-btn"
        onClick={() => handleEditModalOpen(term)}
      >
        <i className="fa-solid fa-pen-to-square"></i>
      </button>
    ),
  }));

  return (
    <>
      <Navbar
        pageName="Manage Terms"
        activePage="Manage Terms"
        createButtons={[
          { text: "Create Term", onClick: handleCreateModalOpen },
        ]}
        breadcrumbs={breadcrumbs}
      />

      <div className="manage-users-container">
        <div className="table-section">
          {isLoading ? (
            <Loader style={{ fontSize: "2.2rem" }} />
          ) : (
            <Table
              data={enhancedTerms}
              columns={columns}
              caption={`Terms & Conditions (${terms.length})`}
            />
          )}
        </div>
      </div>

      {/* Create Term Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={handleCreateModalClose}>
        <h2>Create New Term</h2>
        <form onSubmit={handleCreateSubmit} className="term-form">
          <div className="form-group">
            <label htmlFor="create-version">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Version
            </label>
            <input
              type="text"
              id="create-version"
              name="version"
              placeholder="Enter version (e.g. v1.0)"
              value={newTerm?.version}
              onChange={handleCreateInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="create-content">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Content File
            </label>
            <input
              type="file"
              id="create-content"
              name="content"
              accept=".pdf,.doc,.docx"
              onChange={handlecreateFileChange}
              required
            />
            <small className="helper-text">
              Upload a PDF or Word document containing the terms content
            </small>
          </div>
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader style={{ fontSize: "1em", color: "white", top: "0", left: "0" }} />
            ) : (
              <>
                <i className="fa-solid fa-plus"></i> Create Term
              </>
            )}
          </button>
        </form>
      </Modal>

      {/* Edit Term Modal */}
      <Modal isOpen={isEditModalOpen} onClose={handleEditModalClose}>
        <h2>Edit Term</h2>
        <form onSubmit={handleEditSubmit} className="term-form">
          <div className="form-group">
            <label htmlFor="edit-version">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Version
            </label>
            <input
              type="text"
              id="edit-version"
              name="version"
              placeholder="Enter version"
              value={editTerm?.version}
              onChange={handleEditInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-content">
              Content File <span className="optional-text">(Optional)</span>
            </label>
            <input
              type="file"
              id="edit-content"
              name="content"
              accept=".pdf,.doc,.docx"
              onChange={handleEditFileChange}
            />
            {editTerm?.fileURL && !editTermFile && (
              <div className="current-file">
                <p>
                  Current File:{" "}
                  <a
                    href={editTerm?.fileURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contract-link"
                  >
                    {editTerm?.fileName ? editTerm?.fileName : "View PDF"}
                  </a>
                </p>
              </div>
            )}
            <small className="helper-text">
              Upload a new file to replace the current one
            </small>
          </div>
          <button
            type="submit"
            className="btn-submit"
            disabled={!isEditModalChanged || isSubmitting}
          >
            {isSubmitting ? (
              <Loader style={{ fontSize: "1em", color: "white", top: "0", left: "0" }} />
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk"></i> Save Changes
              </>
            )}
          </button>
        </form>
      </Modal>
    </>
  );
};

export default ManageTerms;
